import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, RefreshCw, Users, Package, Mail, Download, Copy, Check } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminBarbadosReservationsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ reservations: [], count: 0, total_books_reserved: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);

  const fetchReservations = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/admin/barbados/reservations`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load reservations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, []);

  const formatDate = (s) =>
    new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  const handleCopyAllEmails = () => {
    const emails = data.reservations.map((r) => r.email).join(", ");
    navigator.clipboard.writeText(emails);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  const handleDownloadCSV = () => {
    const header = "Name,Email,Quantity,Status,Reserved At\n";
    const rows = data.reservations
      .map((r) => `"${(r.name || "").replace(/"/g, '""')}","${r.email}",${r.quantity},${r.status},"${formatDate(r.created_at)}"`)
      .join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `barbados-reservations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const bulkMailto = () => {
    const emails = data.reservations.map((r) => r.email).join(",");
    const subject = encodeURIComponent("Barbados orders are now OPEN — The Detective Diaries of Marchfield Meadows");
    const body = encodeURIComponent(
      `Hi there,\n\nGreat news! Barbados local inventory has arrived and orders are officially open. Visit https://farmsmartkids.com/order/barbados to confirm your copies and arrange pickup or delivery.\n\nThank you for being one of our first reservers — your priority pickup slot and exclusive bookmark + sticker pack are waiting!\n\nFarmSmart Kids\n`
    );
    window.location.href = `mailto:?bcc=${emails}&subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <button
              onClick={() => navigate("/admin/dashboard")}
              className="text-[#1D3557] font-bold flex items-center gap-2 mb-2 hover:text-[#E63946]"
              data-testid="back-dashboard-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Dashboard
            </button>
            <h1 className="text-3xl font-black text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Barbados Reservations
            </h1>
            <p className="text-[#1D3557] font-medium">Early-access waitlist before Barbados orders officially open</p>
          </div>
          <button
            onClick={fetchReservations}
            className="btn-secondary text-base py-3 px-6 inline-flex items-center gap-2"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-5 h-5" />
            Refresh
          </button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="tactile-card p-6"
            data-testid="stat-reservations"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#2A9D8F] flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-black text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  {data.count}
                </p>
                <p className="text-sm text-[#1D3557]/70 font-bold uppercase tracking-wider">Reservations</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="tactile-card p-6"
            data-testid="stat-books"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#E63946] flex items-center justify-center">
                <Package className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-black text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  {data.total_books_reserved}
                </p>
                <p className="text-sm text-[#1D3557]/70 font-bold uppercase tracking-wider">Books to ship</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="tactile-card p-6"
            data-testid="stat-avg"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#457B9D] flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-black text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  {data.count > 0 ? (data.total_books_reserved / data.count).toFixed(1) : "0"}
                </p>
                <p className="text-sm text-[#1D3557]/70 font-bold uppercase tracking-wider">Avg copies / reserver</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bulk actions */}
        {data.count > 0 && (
          <div className="flex flex-wrap gap-3 mb-6" data-testid="bulk-actions">
            <button
              onClick={bulkMailto}
              className="btn-primary text-sm py-2 px-4 inline-flex items-center gap-2"
              data-testid="bulk-email-btn"
            >
              <Mail className="w-4 h-4" />
              Email all reservers
            </button>
            <button
              onClick={handleCopyAllEmails}
              className="btn-teal text-sm py-2 px-4 inline-flex items-center gap-2"
              data-testid="copy-emails-btn"
            >
              {copiedAll ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy all emails</>}
            </button>
            <button
              onClick={handleDownloadCSV}
              className="btn-secondary text-sm py-2 px-4 inline-flex items-center gap-2"
              data-testid="download-csv-btn"
            >
              <Download className="w-4 h-4" />
              Download CSV
            </button>
          </div>
        )}

        {error && (
          <div className="bg-[#E63946]/10 border-4 border-[#E63946] rounded-2xl p-4 mb-6" data-testid="error">
            <p className="text-[#E63946] font-medium">{error}</p>
          </div>
        )}

        {/* Reservation table */}
        <div className="tactile-card overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="w-16 h-16 border-4 border-[#E63946] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : data.reservations.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-16 h-16 text-[#1D3557]/30 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#1D3557] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                No reservations yet
              </h3>
              <p className="text-[#1D3557]/70 font-medium">
                Once a Barbados visitor signs up via the early-access form, they'll appear here.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="reservations-table">
                <thead className="bg-[#1D3557] text-white">
                  <tr>
                    <th className="text-left px-4 py-3 font-bold text-sm uppercase tracking-wider">#</th>
                    <th className="text-left px-4 py-3 font-bold text-sm uppercase tracking-wider">Name</th>
                    <th className="text-left px-4 py-3 font-bold text-sm uppercase tracking-wider">Email</th>
                    <th className="text-right px-4 py-3 font-bold text-sm uppercase tracking-wider">Qty</th>
                    <th className="text-left px-4 py-3 font-bold text-sm uppercase tracking-wider">Reserved</th>
                  </tr>
                </thead>
                <tbody>
                  {data.reservations.map((r, idx) => (
                    <tr
                      key={r.id}
                      className={`border-b border-[#1D3557]/10 ${idx % 2 === 0 ? "bg-white" : "bg-[#FDFBF7]"} hover:bg-[#E9C46A]/10`}
                      data-testid={`reservation-row-${idx}`}
                    >
                      <td className="px-4 py-3 text-[#1D3557]/60 font-mono text-sm">{idx + 1}</td>
                      <td className="px-4 py-3 text-[#1D3557] font-bold">{r.name}</td>
                      <td className="px-4 py-3">
                        <a href={`mailto:${r.email}`} className="text-[#E63946] font-bold hover:underline">
                          {r.email}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="inline-block px-3 py-1 rounded-full bg-[#E9C46A]/30 text-[#1D3557] font-bold">
                          {r.quantity}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#1D3557]/70 font-medium text-sm">{formatDate(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBarbadosReservationsPage;
